import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Seo } from "../components/Seo";
import { faqLd, breadcrumbLd, webAppLd, howToLd } from "../components/Seo";
import { fetchVisaCountries, fetchVisaInfo } from "../lib/api";
import { track } from "../lib/analytics";
import {
  IdentificationCard,
  CurrencyInr,
  Clock,
  CalendarBlank,
  ArrowSquareOut,
  CaretDown,
  Check,
  X,
  Warning,
  Lightning,
  Camera,
  Bank,
  Buildings,
  Sparkle,
  ArrowRight,
  Stack,
  Question,
  Lifebuoy,
  Plugs,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_META = {
  visa_free: { color: "#3DB68B", label: "Visa-free" },
  voa: { color: "#2A7FB8", label: "Visa on Arrival" },
  eta: { color: "#2A7FB8", label: "ETA / Online" },
  evisa: { color: "#7C6CE0", label: "e-Visa" },
  sticker: { color: "#FF7E4D", label: "Sticker visa" },
};

const DIFFICULTY_COLOR = {
  1: "#3DB68B",
  2: "#2A7FB8",
  3: "#7C6CE0",
  4: "#FF7E4D",
  5: "#E0457B",
};

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

export default function Visa() {
  const navigate = useNavigate();
  const [allCountries, setAllCountries] = useState([]);
  const [selected, setSelected] = useState(["thailand"]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState({}); // key = slug:idx

  // Load country list on mount
  useEffect(() => {
    fetchVisaCountries()
      .then(setAllCountries)
      .catch(() => toast.error("Couldn't load visa list"));
  }, []);

  const groupedByRegion = useMemo(() => {
    const map = {};
    for (const c of allCountries) {
      (map[c.region] = map[c.region] || []).push(c);
    }
    return map;
  }, [allCountries]);

  const toggle = (slug) => {
    setSelected((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= 5) {
        toast.error("Max 5 countries per chain");
        return prev;
      }
      return [...prev, slug];
    });
  };

  const run = async () => {
    if (!selected.length) {
      toast.error("Pick at least one country");
      return;
    }
    track("visa_check_started", { countries: selected });
    setLoading(true);
    setResult(null);
    try {
      const data = await fetchVisaInfo(selected);
      setResult(data);
      track("visa_check_completed", {
        countries: selected,
        total_fee_inr: data?.chain_summary?.total_fee_inr_approx,
      });
      setTimeout(
        () =>
          document
            .getElementById("visa-result")
            ?.scrollIntoView({ behavior: "smooth", block: "start" }),
        160,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't fetch visa intel. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  // Build structured data: aggregate all FAQs across selected countries +
  // breadcrumb + WebApplication. Massive SEO win for "thailand visa for indians"
  // type queries because Google shows FAQ-rich results directly in SERP.
  const structuredData = useMemo(() => {
    const out = [
      webAppLd({
        name: "Flycher Visa Wizard",
        description:
          "Free visa intel for Indian passport holders — fees in INR, processing time, documents, and FAQs for 19 countries.",
        path: "/visa",
      }),
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Visa Wizard", path: "/visa" },
      ]),
    ];
    if (result?.countries?.length) {
      const allFaqs = [];
      result.countries.forEach((c) => {
        (c.faqs || []).forEach((f) => {
          if (f?.q && f?.a) {
            allFaqs.push({ question: `${f.q} (${c.country})`, answer: f.a });
          }
        });
      });
      if (allFaqs.length) out.push(faqLd(allFaqs));
      // Add HowTo for the first country's application steps
      const first = result.countries[0];
      if (first?.application_steps?.length) {
        out.push(
          howToLd({
            name: `How to apply for a ${first.country} visa from India`,
            description: `Step-by-step application process for Indian passport holders applying for a ${first.country} ${first.visa_type_label}.`,
            steps: first.application_steps.map((s, i) => ({
              name: `Step ${i + 1}`,
              text: s,
            })),
          }),
        );
      }
    } else {
      // Baseline FAQs always present so crawlers see FAQ rich-snippet schema
      // even on the empty/initial state of the page.
      out.push(
        faqLd([
          { question: "Which countries are visa-free for Indian passport holders?", answer: "Currently visa-free for Indians: Thailand (60 days), Malaysia (30 days), Maldives (30 days on arrival), Nepal (unlimited, no passport needed), Bhutan, Mauritius, and a few Caribbean nations. Thailand's visa-free is the easiest popular destination for Indians right now." },
          { question: "How long does an e-Visa take for Indians?", answer: "It varies: Vietnam e-Visa takes 3-5 business days, Sri Lanka ETA is instant to 24 hours, Oman e-Visa 1-3 days, UAE e-Visa 3-5 days, Australia eVisitor 20-30 days. Most e-Visas under ₹5,000." },
          { question: "What documents do Indians need for a tourist visa?", answer: "Standard list: passport with 6-month validity, recent photos meeting consulate specs, return flight ticket, confirmed hotel bookings, bank statements (last 3-6 months showing ₹50,000-₹1,00,000), ITR or Form 16, employer NOC or business registration, cover letter, travel insurance for higher-difficulty visas, and any prior visa copies." },
          { question: "Why do Indian visa applications get rejected?", answer: "Common reasons: insufficient bank balance, unclear travel purpose, weak ties to India (no job/property proof), photo not meeting spec, prior overstay, inconsistent travel history. Apply with realistic dates and complete documentation to avoid rejection." },
          { question: "How much does a Thailand visa cost for Indians?", answer: "Thailand is currently visa-free for Indian tourists for up to 60 days — ₹0 cost. You just need a return ticket, hotel booking, and ~10,000 baht (₹24,000) cash or card equivalent at immigration." },
          { question: "Can I apply for a Schengen visa from India in advance?", answer: "Yes, you can apply up to 6 months before your travel date. Schengen visa processing takes 15-30 working days, costs around ₹7,500 (EUR 80 + VFS fees), and you apply at the consulate of the country where you'll spend the longest. Recommended to apply 4-8 weeks ahead." },
        ]),
      );
    }
    return out;
  }, [result]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF7F0]">
      <Seo
        title={
          result?.countries?.length === 1
            ? `${result.countries[0].country} visa for Indians — fees, docs, FAQs`
            : "Visa wizard for Indians — fees, docs, FAQs · Flycher"
        }
        description={
          result?.countries?.length === 1
            ? `Everything Indian passport-holders need for a ${result.countries[0].country} visa: ${result.countries[0].visa_type_label}, INR fee, processing time, 8-12 documents, common rejection reasons, and 10+ FAQs.`
            : "Instant visa intel for Indian passport-holders — visa type, INR cost, processing days, documents, FAQs, and multi-country chain check. e-Visa, VoA, sticker, all in one place."
        }
        path="/visa"
        jsonLd={structuredData}
      />
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto w-full px-5 sm:px-8 pt-28 pb-20">
        <span className="kicker">— Visa wizard · Indian passport</span>
        <h1 className="font-serif text-4xl sm:text-6xl mt-2 text-slate-900 leading-tight">
          Every visa answer,
          <br />
          <span className="italic" style={{ color: "#7C6CE0" }}>
            for an Indian passport.
          </span>
        </h1>
        <p className="text-slate-600 mt-4 max-w-2xl">
          Pick one country to see everything — or pick up to <strong>5</strong> for a chain check
          with smart application order, shared documents, and total INR cost.
        </p>

        {/* PICKER */}
        <section
          data-testid="visa-picker"
          className="mt-8 rounded-2xl bg-white border border-slate-200 shadow-sm p-5 sm:p-6"
        >
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <span className="kicker">— Tap countries (max 5)</span>
            <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
              {selected.length} selected
            </span>
          </div>

          {Object.entries(groupedByRegion).map(([region, list]) => (
            <div key={region} className="mt-4 first:mt-0">
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-2">
                {region}
              </p>
              <div className="flex flex-wrap gap-2">
                {list.map((c) => {
                  const active = selected.includes(c.slug);
                  const meta = TYPE_META[c.visa_type] || TYPE_META.evisa;
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      data-testid={`visa-pick-${c.slug}`}
                      onClick={() => toggle(c.slug)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition ${
                        active
                          ? "text-white border-transparent shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                      style={active ? { background: meta.color } : {}}
                    >
                      <span className="text-base leading-none">{c.flag}</span>
                      <span className="font-medium">{c.name}</span>
                      {active && <Check size={12} weight="bold" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={run}
              disabled={loading || !selected.length}
              data-testid="visa-run-btn"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white shadow-lg shadow-purple-200/60 disabled:opacity-50"
              style={{ background: "#7C6CE0" }}
            >
              <IdentificationCard size={16} weight="bold" />
              {loading
                ? "Pulling visa intel…"
                : selected.length > 1
                ? `Run chain check (${selected.length})`
                : "Get visa intel"}
            </button>
          </div>
        </section>

        {loading && (
          <div className="mt-10 rounded-2xl bg-white border border-slate-100 p-8 text-center">
            <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#7C6CE0] border-t-transparent animate-spin" />
            <p className="mt-4 text-sm text-slate-600 font-mono uppercase tracking-wider">
              Compiling docs · FAQs · application steps…
            </p>
          </div>
        )}

        {result && !loading && (
          <Result
            data={result}
            openFaq={openFaq}
            setOpenFaq={setOpenFaq}
            navigate={navigate}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

function Result({ data, openFaq, setOpenFaq, navigate }) {
  const countries = data?.countries || [];
  const chain = data?.chain_summary || {};
  const isChain = countries.length > 1;
  const orderMap = useMemo(() => {
    const map = {};
    (chain.recommended_application_order || []).forEach((s, i) => (map[s] = i + 1));
    return map;
  }, [chain]);

  const orderedCountries = useMemo(() => {
    if (!chain.recommended_application_order?.length) return countries;
    const bySlug = Object.fromEntries(countries.map((c) => [c.slug, c]));
    return chain.recommended_application_order.map((s) => bySlug[s]).filter(Boolean);
  }, [countries, chain]);

  return (
    <section id="visa-result" data-testid="visa-result" className="mt-12 space-y-8">
      {/* CHAIN SUMMARY (only when 2+) */}
      {isChain && (
        <div
          data-testid="visa-chain"
          className="rounded-2xl p-6 sm:p-8 text-white shadow-xl"
          style={{ background: "linear-gradient(135deg, #7C6CE0 0%, #2A7FB8 100%)" }}
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/85">
            Chain check · {countries.length} countries
          </span>
          <h2 className="font-serif text-3xl sm:text-5xl mt-2 leading-tight">
            Total visa cost:{" "}
            <span className="font-bold">
              {fmtINR(chain.total_fee_inr_approx)}
            </span>
          </h2>
          {chain.total_processing_days_estimate && (
            <p className="text-sm sm:text-base mt-2 text-white/95">
              Combined processing: <strong>{chain.total_processing_days_estimate}</strong>
            </p>
          )}

          <div className="mt-5 rounded-xl bg-white/12 backdrop-blur p-4">
            <span className="kicker text-white/90">— Apply in this order</span>
            <ol className="mt-2 flex flex-wrap gap-2">
              {(chain.recommended_application_order || []).map((s, i) => {
                const c = countries.find((x) => x.slug === s);
                if (!c) return null;
                return (
                  <li
                    key={s}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 text-sm"
                  >
                    <span className="font-mono text-[11px] text-white/75">{i + 1}</span>
                    <span>{c.flag}</span>
                    <span className="font-medium">{c.country}</span>
                  </li>
                );
              })}
            </ol>
            {chain.order_reason && (
              <p className="text-xs text-white/90 mt-3 leading-relaxed">{chain.order_reason}</p>
            )}
          </div>

          {chain.shared_documents?.length > 0 && (
            <div className="mt-4">
              <span className="kicker text-white/90">— Documents reusable across all</span>
              <ul className="mt-2 grid sm:grid-cols-2 gap-1.5">
                {chain.shared_documents.map((d, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <Stack size={14} weight="duotone" className="mt-1 flex-shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {chain.smart_tips?.length > 0 && (
            <ul className="mt-4 space-y-1.5">
              {chain.smart_tips.map((t, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <Lightning size={14} weight="fill" className="mt-1 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* PER-COUNTRY DETAIL */}
      {orderedCountries.map((c) => (
        <CountryCard
          key={c.slug}
          c={c}
          order={orderMap[c.slug]}
          openFaq={openFaq}
          setOpenFaq={setOpenFaq}
        />
      ))}

      {/* CTAs */}
      <div className="flex flex-wrap gap-3 pt-2">
        <button
          type="button"
          data-testid="visa-build-itinerary"
          onClick={() => {
            const first = countries[0]?.slug;
            if (first) navigate(`/itinerary/${first}`);
            else navigate("/itinerary");
          }}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-white shadow-md"
          style={{ background: "#FF7E4D" }}
        >
          <Sparkle size={14} weight="fill" />
          Now plan the trip
          <ArrowRight size={12} weight="bold" />
        </button>
        <button
          type="button"
          data-testid="visa-see-budget"
          onClick={() => navigate("/budget")}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:border-[#3DB68B] hover:text-[#3DB68B]"
        >
          <CurrencyInr size={14} weight="bold" />
          Estimate trip budget
          <ArrowRight size={12} weight="bold" />
        </button>
      </div>

      <div className="rounded-xl bg-[#FFF6E5] border border-[#F4B942]/30 p-4 flex items-start gap-3">
        <Warning size={18} weight="fill" color="#F4B942" className="flex-shrink-0 mt-0.5" />
        <p className="text-xs text-slate-700 leading-relaxed">
          <strong>{data.disclaimer}</strong>
        </p>
      </div>
    </section>
  );
}

function CountryCard({ c, order, openFaq, setOpenFaq }) {
  const typeMeta = TYPE_META[c.visa_type] || TYPE_META.evisa;
  const diffColor = DIFFICULTY_COLOR[c.difficulty] || "#7C6CE0";

  return (
    <article
      data-testid={`visa-country-${c.slug}`}
      className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
    >
      {/* Header */}
      <div
        className="p-6 sm:p-7 text-white"
        style={{
          background: `linear-gradient(135deg, ${typeMeta.color} 0%, ${typeMeta.color}CC 100%)`,
        }}
      >
        <div className="flex items-start gap-4 flex-wrap">
          <div className="text-5xl leading-none">{c.flag}</div>
          <div className="flex-1 min-w-[200px]">
            {order && (
              <span className="inline-block text-[10px] font-mono uppercase tracking-[0.2em] text-white/80 mb-1">
                Step {order} · {c.region}
              </span>
            )}
            <h3 className="font-serif text-2xl sm:text-4xl leading-tight">{c.country}</h3>
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-xs font-medium">
                {c.visa_type_label}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 text-xs font-medium">
                {c.difficulty_label} · {c.difficulty}/5
              </span>
            </div>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            Icon={CurrencyInr}
            label="Approx fee"
            value={c.fee_inr_approx === 0 ? "Free" : fmtINR(c.fee_inr_approx)}
            sub={c.fee_native}
          />
          <Stat
            Icon={Clock}
            label="Processing"
            value={c.processing.split(" ").slice(0, 3).join(" ")}
            sub={c.processing}
          />
          <Stat
            Icon={CalendarBlank}
            label="Max stay"
            value={
              typeof c.max_stay_days === "number" ? `${c.max_stay_days} days` : c.max_stay_days
            }
            sub={c.entry_type}
          />
          <Stat
            Icon={IdentificationCard}
            label="Passport"
            value={
              c.passport_validity_months
                ? `${c.passport_validity_months}-mo validity`
                : "Not required"
            }
            sub={c.validity}
          />
        </div>
      </div>

      {/* Body */}
      <div className="p-6 sm:p-8 space-y-7">
        {/* Documents */}
        {c.documents_required.length > 0 && (
          <Block icon={Stack} color={diffColor} title="Documents required">
            <ul className="grid sm:grid-cols-2 gap-2">
              {c.documents_required.map((d, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-700 flex items-start gap-2 leading-relaxed"
                >
                  <Check
                    size={14}
                    weight="bold"
                    className="mt-1 flex-shrink-0"
                    color={diffColor}
                  />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {/* Quick facts grid */}
        <div className="grid sm:grid-cols-2 gap-3">
          {c.photo_specs && (
            <Fact Icon={Camera} color="#2A7FB8" label="Photo specs" value={c.photo_specs} />
          )}
          {c.proof_of_funds_inr && (
            <Fact
              Icon={Bank}
              color="#3DB68B"
              label="Bank balance"
              value={c.proof_of_funds_inr}
            />
          )}
          <Fact
            Icon={c.onward_ticket_required ? Check : X}
            color={c.onward_ticket_required ? "#FF7E4D" : "#94A3B8"}
            label="Onward / return ticket"
            value={
              c.onward_ticket_required
                ? "Required — carry printout / e-ticket"
                : "Not strictly required"
            }
          />
          <Fact
            Icon={c.insurance_required ? Check : X}
            color={c.insurance_required ? "#FF7E4D" : "#94A3B8"}
            label="Travel insurance"
            value={
              c.insurance_required
                ? "Mandatory — minimum coverage as per consulate"
                : "Recommended, not mandatory"
            }
          />
          {c.yellow_fever_cert_required && (
            <Fact
              Icon={Warning}
              color="#E0457B"
              label="Yellow fever cert"
              value="Required if arriving from yellow-fever zones (Africa/Latin America)."
            />
          )}
          {c.covid_requirements && (
            <Fact Icon={Lifebuoy} color="#7C6CE0" label="Health rules" value={c.covid_requirements} />
          )}
        </div>

        {/* Application steps */}
        {c.application_steps.length > 0 && (
          <Block icon={Plugs} color="#2A7FB8" title="Step-by-step application">
            <ol className="space-y-2.5">
              {c.application_steps.map((s, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-3 leading-relaxed">
                  <span
                    className="w-6 h-6 rounded-full flex-shrink-0 grid place-items-center text-white text-xs font-bold"
                    style={{ background: "#2A7FB8" }}
                  >
                    {i + 1}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </Block>
        )}

        {/* Common rejection reasons */}
        {c.common_rejection_reasons.length > 0 && (
          <Block icon={Warning} color="#E0457B" title="Common rejection reasons">
            <ul className="space-y-2">
              {c.common_rejection_reasons.map((r, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2 leading-relaxed">
                  <X size={14} weight="bold" color="#E0457B" className="mt-1 flex-shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {/* Pro tips */}
        {c.pro_tips.length > 0 && (
          <div className="rounded-2xl bg-[#FFF1E8] border border-[#FF7E4D]/20 p-5">
            <div className="flex items-center gap-2">
              <Lightning size={16} weight="fill" color="#FF7E4D" />
              <span className="kicker">— Pro tips for Indian travelers</span>
            </div>
            <ul className="mt-3 space-y-2">
              {c.pro_tips.map((t, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-800 flex items-start gap-2 leading-relaxed"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 bg-[#FF7E4D]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FAQs accordion */}
        {c.faqs.length > 0 && (
          <Block icon={Question} color="#7C6CE0" title={`Frequently asked (${c.faqs.length})`}>
            <div className="space-y-2">
              {c.faqs.map((f, i) => {
                const key = `${c.slug}:${i}`;
                const open = !!openFaq[key];
                return (
                  <div
                    key={i}
                    data-testid={`visa-faq-${c.slug}-${i}`}
                    className="rounded-xl border border-slate-200 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setOpenFaq((prev) => ({ ...prev, [key]: !prev[key] }))
                      }
                      className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
                    >
                      <CaretDown
                        size={14}
                        weight="bold"
                        className={`text-slate-400 transition-transform ${
                          open ? "rotate-180" : ""
                        }`}
                      />
                      <span className="text-sm font-medium text-slate-800 flex-1">{f.q}</span>
                    </button>
                    {open && (
                      <div className="px-4 pb-4 pt-1 text-sm text-slate-600 leading-relaxed">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Block>
        )}

        {/* Portal + embassy */}
        <div className="grid sm:grid-cols-2 gap-3">
          <a
            href={c.official_portal}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`visa-portal-${c.slug}`}
            className="rounded-2xl bg-slate-900 text-white p-5 flex items-start gap-3 hover:bg-slate-800 transition-colors group"
          >
            <ArrowSquareOut
              size={20}
              weight="duotone"
              className="flex-shrink-0 mt-0.5 group-hover:translate-x-0.5 transition-transform"
            />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-white/70">
                Official portal
              </p>
              <p className="text-sm font-semibold mt-0.5 break-all">{c.official_portal}</p>
            </div>
          </a>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-3">
            <Buildings size={20} weight="duotone" color="#2A7FB8" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
                Embassy / mission in India
              </p>
              <p className="text-sm text-slate-800 mt-0.5">{c.embassy_in_india}</p>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function Stat({ Icon, label, value, sub }) {
  return (
    <div className="rounded-xl bg-white/15 backdrop-blur p-3">
      <div className="flex items-center gap-1.5">
        <Icon size={13} weight="duotone" className="opacity-90" />
        <span className="text-[10px] font-mono uppercase tracking-wider text-white/80">
          {label}
        </span>
      </div>
      <p className="font-serif text-lg sm:text-xl mt-0.5 font-semibold leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-white/75 mt-0.5 leading-snug">{sub}</p>}
    </div>
  );
}

function Block({ icon: Icon, color, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="duotone" color={color} />
        <span className="kicker">— {title}</span>
      </div>
      {children}
    </div>
  );
}

function Fact({ Icon, color, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3.5 flex items-start gap-3">
      <div
        className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0"
        style={{ background: color + "1A", color }}
      >
        <Icon size={14} weight="duotone" />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
        <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{value}</p>
      </div>
    </div>
  );
}
